<template>
  <div class="signature-box">
    <template v-if="type === 'draw'">
      <canvas
        ref="canvas"
        class="signature-box__canvas"
        :class="{ 'is-disabled': isDisabled }"
      />
      <div class="signature-box__canvas__buttons">
        <BaseButton
          v-if="drawnSignature"
          @click.native="clearCanvas"
          variant="danger"
          size="small"
          class="signature-box__canvas__clear"
        >
          {{ $t('signatureBox.clear') }}
        </BaseButton>
      </div>
      <message v-if="isDisabled" type="warning" iconName="warning">
        {{ $t('signatureBox.clearFirst') }}
      </message>
    </template>
    <template v-if="type === 'type'">
      <div class="signature-box__type">
        <input :value="typedSignature" @input="handleInput" type="text"/>
        <div class="signature-box__type__input">
          {{ typedSignature ? typedSignature : $t('signatureBox.yourSignature') }}
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BaseButton from '@/components/library/buttons/BaseButton.vue'
import SignaturePad from 'signature_pad/dist/signature_pad'
import Message from '@/components/Message'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'signature-box',
  props: ['type', 'currentField'],
  components: {
    BaseButton,
    Message
  },
  data () {
    return {
      drawnSignature: this.$store.getters.signatures.drawn,
      typedSignature: this.$store.getters.signatures.typed,
      isDisabled: false,
      opts: {
        dragging: false
      },
      signaturePad: null
    }
  },
  mounted () {
    if (this.type === 'draw') {
      this.fitCanvas()

      this.signaturePad = new SignaturePad(this.$refs.canvas, {
        backgroundColor: 'rgb(255, 255, 255)'
      })

      this.signaturePad.addEventListener('endStroke', this.handleStroke)
      window.addEventListener('resize', this.redrawCanvas)
    }

    this.$nextTick(() => {
      this.initSignatures()
    })
  },
  destroyed () {
    if (this.type === 'draw') {
      this.signaturePad.removeEventListener('endStroke', this.handleStroke)
      window.removeEventListener('resize', this.redrawCanvas)
    }
  },
  computed: {
    ...mapGetters(['latestSignature'])
  },
  methods: {
    ...mapActions(['setLatestSignature']),
    initSignatures () {
      this.typedSignature = this.currentField.content || this.latestSignature.content
      this.drawnSignature = this.currentField.drawn_json
        ? this.currentField.drawn_json[0]
        : this.latestSignature.drawn_json
          ? this.latestSignature.drawn_json[0]
          : null

      if (this.signaturePad && this.drawnSignature) {
        this.isDisabled = true
        this.signaturePad.fromData(this.drawnSignature)
        this.signaturePad.off()
      }
    },
    handleInput (event) {
      const newSignature = event.target.value
      const oldSignature = this.typedSignature

      if (!newSignature || !newSignature.trim().length) {
        this.typedSignature = null
      } else {
        this.typedSignature = newSignature
      }

      if (
        (!newSignature && oldSignature) ||
        (newSignature && !oldSignature) ||
        (newSignature && oldSignature && newSignature !== oldSignature)
      ) {
        this.setLatestSignature({ property: 'content', value: newSignature })
      }
    },
    handleStroke () {
      // rebuild signature pad in case it has changed size
      this.rebuildSignaturePad()
      // get images
      const svgSignature = this.signaturePad.toDataURL('image/svg+xml')
      const jpgSignature = this.signaturePad.toDataURL('image/jpeg')

      this.setLatestSignature({ property: 'drawn_json', value: [this.drawnSignature, svgSignature, jpgSignature] })
    },
    clearCanvas () {
      this.drawnSignature = null
      this.setLatestSignature({ property: 'drawn_json', value: null })
      this.signaturePad.clear()
      this.signaturePad.off()
      this.signaturePad.on()
      this.isDisabled = false
    },
    /**
     * Fit canvas to size in document
     */
    fitCanvas () {
      const canvas = this.$refs.canvas
      const ctx = canvas.getContext('2d')
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = canvas.offsetWidth * ratio
      canvas.height = canvas.offsetHeight * ratio
      ctx.scale(ratio, ratio)
    },
    redrawCanvas () {
      this.fitCanvas()
      this.rebuildSignaturePad()
    },
    /**
     * Rebuilds signature pad
     *
     * When the canvas has changed size it's content is wiped,
     * this repopulates it with current drawn data
     */
    rebuildSignaturePad () {
      this.drawnSignature = this.signaturePad.toData()
      this.signaturePad.fromData(this.drawnSignature)
    }
  }
}
</script>

<style lang="sass" scoped>
  @import '@/styles/component-utils.sass'
  .signature-box
    position: relative
    &__canvas
      border-bottom: 4px solid $color-border-dark
      width: 100%
      height: 250px
      display: block
      &__clear
        position: absolute
        top: 0
        right: 0
    &__type
      &__input
        font-family: 'danielregular'
        font-size: $font-size-heading
        border-top: 1px solid $color-border-dark
        padding: $spacing-base
      input
        width: 100%
        border: 0
        margin: 0
        display: block
        padding: $spacing-base
        font-size: $font-size-heading
    &__upload
      display: block
      border: 3px dashed $color-border-dark
      padding: $spacing-base
      min-height: 250px
      position: relative
      cursor: pointer
      &__icon
        position: absolute
        top: 50%
        left: 50%
        margin-right: 0
        margin-left: -150px
        margin-top: -50px
        text-align: center
        width: 300px
        height: 100px
      svg
        fill: $color-border-dark
      p
        margin: 0
        color: $color-border-dark
        font-weight: bold
      input
        display: none
      &--dragging
        border-color: darken($color-border-dark, 20%)
        svg
          fill: darken($color-border-dark, 20%)
        p
          color: darken($color-border-dark, 20%)
    &__image-placeholder
      border: 1px solid $color-border-dark
      position: relative
      min-height: 250px
      overflow: hidden
      svg
        margin-right: 0
        position: absolute
        left: 50%
        top: 50%
        margin-left: -30px
        margin-top: -30px
      img
        position: absolute
        top: 50%
        transform: translate3d(0, -50%, 0)
    .message
      padding: $spacing-small
      margin-top: $spacing-base
      border: 1px solid $color-warning
      background-color: #fff
      color: $color-warning
  // Responsive
  @media screen and (max-width: $screen-size-small)
    .signature-box
      &__canvas
        height: 100px
      &__type
        &__input
          display: none
        input
          font-family: 'danielregular'
          padding: $spacing-small
          font-size: 25px
          border: 1px solid $color-border
          width: 100%
  @media all and (max-width: $screen-size-medium) and (orientation : landscape)
    .signature-box
      &__canvas
        height: 100px
      &__type
        &__input
          display: none
        input
          font-family: 'danielregular'
          padding: $spacing-small
          font-size: 25px
          margin: -$spacing-base
          width: 100%
</style>
